import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import QuoteModal from './QuoteModal';

const QuotesTab = () => {
  const { user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [projectTypeFilter, setProjectTypeFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);

  useEffect(() => {
    if (user?.id) {
      loadQuotes();
    }
  }, [user]);

  useEffect(() => {
    filterQuotes();
  }, [quotes, searchTerm, statusFilter, projectTypeFilter]);

  const loadQuotes = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase?.from('provider_quotes')?.select('*')?.eq('provider_id', user?.id)?.order('quote_date', { ascending: false });

      if (error) throw error;
      setQuotes(data || []);
    } catch (error) {
      console.error('Error loading quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterQuotes = () => {
    let filtered = [...quotes];

    if (searchTerm) {
      filtered = filtered?.filter(
        (q) =>
          q?.customer_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
          q?.customer_email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
          q?.quote_number?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered?.filter((q) => q?.status === statusFilter);
    }

    if (projectTypeFilter !== 'all') {
      filtered = filtered?.filter((q) => q?.project_type === projectTypeFilter);
    }

    setFilteredQuotes(filtered);
  };

  const handleAddQuote = () => {
    setSelectedQuote(null);
    setIsModalOpen(true);
  };

  const handleEditQuote = (quote) => {
    setSelectedQuote(quote);
    setIsModalOpen(true);
  };

  const handleDeleteQuote = async (quoteId) => {
    if (!window.confirm('Weet u zeker dat u deze offerte wilt verwijderen?')) return;

    try {
      const { error } = await supabase?.from('provider_quotes')?.delete()?.eq('id', quoteId)?.eq('provider_id', user?.id);

      if (error) throw error;
      loadQuotes();
    } catch (error) {
      console.error('Error deleting quote:', error);
    }
  };

  const handleDuplicateQuote = async (quote) => {
    try {
      const newQuote = {
        ...quote,
        id: undefined,
        quote_number: `${quote?.quote_number}-COPY`,
        status: 'concept',
        quote_date: new Date()?.toISOString()?.split('T')?.[0],
      };

      const { error } = await supabase?.from('provider_quotes')?.insert(newQuote)?.select();

      if (error) throw error;
      loadQuotes();
    } catch (error) {
      console.error('Error duplicating quote:', error);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      concept: 'bg-muted text-muted-foreground border-border',
      verstuurd: 'bg-info/10 text-info border-info/20',
      in_overweging: 'bg-warning/10 text-warning border-warning/20',
      gewonnen: 'bg-success/10 text-success border-success/20',
      verloren: 'bg-error/10 text-error border-error/20',
    };
    const labels = {
      concept: 'Concept',
      verstuurd: 'Verstuurd',
      in_overweging: 'In Overweging',
      gewonnen: 'Gewonnen',
      verloren: 'Verloren',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles?.[status]}`}>
        {labels?.[status]}
      </span>
    );
  };

  const statusOptions = [
    { value: 'all', label: 'Alle Statussen' },
    { value: 'concept', label: 'Concept' },
    { value: 'verstuurd', label: 'Verstuurd' },
    { value: 'in_overweging', label: 'In Overweging' },
    { value: 'gewonnen', label: 'Gewonnen' },
    { value: 'verloren', label: 'Verloren' },
  ];

  const projectTypeOptions = [
    { value: 'all', label: 'Alle Types' },
    { value: 'zonnepanelen', label: 'Zonnepanelen' },
    { value: 'isolatie', label: 'Isolatie' },
    { value: 'warmtepomp', label: 'Warmtepomp' },
    { value: 'ventilatie', label: 'Ventilatie' },
    { value: 'thuisbatterij', label: 'Thuisbatterij' },
    { value: 'waterontharder', label: 'Waterontharder' },
    { value: 'overig', label: 'Overig' },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3]?.map((i) => (
            <div key={i} className="h-16 bg-muted rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground">Offertebeheer</h2>
          <p className="text-muted-foreground mt-1">Beheer al uw offertes op één plek</p>
        </div>
        <Button onClick={handleAddQuote} iconName="Plus">
          Nieuwe Offerte
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            placeholder="Zoek op naam, email of offertenummer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
          />
          <Select
            options={statusOptions}
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="Filter op status"
          />
          <Select
            options={projectTypeOptions}
            value={projectTypeFilter}
            onChange={setProjectTypeFilter}
            placeholder="Filter op type"
          />
        </div>
      </div>

      {/* Quotes Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Offertenummer
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Klant
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Bedrag
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Datum
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Acties
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredQuotes?.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-muted-foreground">
                    Geen offertes gevonden
                  </td>
                </tr>
              ) : (
                filteredQuotes?.map((quote) => (
                  <tr key={quote?.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-4 text-sm font-medium text-foreground">{quote?.quote_number}</td>
                    <td className="px-4 py-4">
                      <div>
                        <p className="text-sm font-medium text-foreground">{quote?.customer_name}</p>
                        <p className="text-xs text-muted-foreground">{quote?.customer_email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-foreground capitalize">{quote?.project_type}</td>
                    <td className="px-4 py-4 text-sm font-medium text-foreground">
                      €{parseFloat(quote?.quote_amount)?.toLocaleString('nl-NL')}
                    </td>
                    <td className="px-4 py-4">{getStatusBadge(quote?.status)}</td>
                    <td className="px-4 py-4 text-sm text-muted-foreground">
                      {new Date(quote?.quote_date)?.toLocaleDateString('nl-NL')}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center space-x-2">
                        <Button size="sm" variant="ghost" onClick={() => handleEditQuote(quote)}>
                          <Icon name="Edit" size={16} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDuplicateQuote(quote)}>
                          <Icon name="Copy" size={16} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteQuote(quote?.id)}>
                          <Icon name="Trash2" size={16} className="text-error" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quote Modal */}
      {isModalOpen && (
        <QuoteModal
          quote={selectedQuote}
          onClose={() => setIsModalOpen(false)}
          onSave={() => {
            setIsModalOpen(false);
            loadQuotes();
          }}
        />
      )}
    </div>
  );
};

export default QuotesTab;